// Nutrition facts calculation utilities

// Basic nutrition data for common ingredients (per 100g)
const nutritionDatabase = {
    // Grains & Flours
    'rice': { calories: 130, protein: 2.7, carbs: 28, fat: 0.3, fiber: 0.4 },
    'flour': { calories: 364, protein: 10, carbs: 76, fat: 1, fiber: 2.7 },
    'sugar': { calories: 387, protein: 0, carbs: 100, fat: 0, fiber: 0 },

    // Proteins
    'chicken': { calories: 165, protein: 31, carbs: 0, fat: 3.6, fiber: 0 },
    'beef': { calories: 250, protein: 26, carbs: 0, fat: 15, fiber: 0 },
    'pork': { calories: 242, protein: 27, carbs: 0, fat: 14, fiber: 0 },
    'fish': { calories: 100, protein: 20, carbs: 0, fat: 2.5, fiber: 0 },
    'egg': { calories: 155, protein: 13, carbs: 1.1, fat: 11, fiber: 0 },

    // Vegetables
    'onion': { calories: 40, protein: 1.1, carbs: 9, fat: 0.1, fiber: 1.7 },
    'garlic': { calories: 149, protein: 6.4, carbs: 33, fat: 0.5, fiber: 2.1 },
    'tomato': { calories: 18, protein: 0.9, carbs: 3.9, fat: 0.2, fiber: 1.2 },
    'carrot': { calories: 41, protein: 0.9, carbs: 10, fat: 0.2, fiber: 2.8 },
    'potato': { calories: 77, protein: 2, carbs: 17, fat: 0.1, fiber: 2.2 },

    // Fruits
    'apple': { calories: 52, protein: 0.3, carbs: 14, fat: 0.2, fiber: 2.4 },
    'banana': { calories: 89, protein: 1.1, carbs: 23, fat: 0.3, fiber: 2.6 },
    'orange': { calories: 47, protein: 0.9, carbs: 12, fat: 0.1, fiber: 2.4 },

    // Dairy
    'milk': { calories: 42, protein: 3.4, carbs: 5, fat: 1, fiber: 0 },
    'cheese': { calories: 113, protein: 7, carbs: 0.4, fat: 9, fiber: 0 },
    'butter': { calories: 717, protein: 0.9, carbs: 0.1, fat: 81, fiber: 0 },

    // Oils & Fats
    'oil': { calories: 884, protein: 0, carbs: 0, fat: 100, fiber: 0 },
    'olive oil': { calories: 884, protein: 0, carbs: 0, fat: 100, fiber: 0 },

    // Seasonings & Sauces
    'salt': { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 },
    'soy sauce': { calories: 60, protein: 8, carbs: 6, fat: 0, fiber: 0 },
    'vinegar': { calories: 18, protein: 0, carbs: 0.9, fat: 0, fiber: 0 },

    // Default for unknown ingredients
    'default': { calories: 100, protein: 2, carbs: 20, fat: 1, fiber: 1 }
};

/**
 * Find the best matching ingredient in the nutrition database
 * @param {string} ingredientName - The ingredient name to match
 * @returns {string} - The best matching key from nutrition database
 */
const findBestMatch = (ingredientName) => {
    const name = ingredientName.toLowerCase();

    // Direct matches
    if (nutritionDatabase[name]) {
        return name;
    }

    // Partial matches
    for (const key of Object.keys(nutritionDatabase)) {
        if (name.includes(key) || key.includes(name)) {
            return key;
        }
    }

    // Check for common variations (Chinese names)
    const variations = {
        'chicken': ['鸡', '鸡肉'],
        'beef': ['牛', '牛肉'],
        'pork': ['猪', '猪肉'],
        'fish': ['鱼', '鱼肉'],
        'rice': ['米', '米饭'],
        'flour': ['面粉'],
        'sugar': ['糖'],
        'oil': ['油'],
        'salt': ['盐'],
        'onion': ['洋葱'],
        'garlic': ['蒜', '大蒜'],
        'tomato': ['番茄', '西红柿'],
        'carrot': ['胡萝卜'],
        'potato': ['土豆', '马铃薯']
    };

    for (const [key, variants] of Object.entries(variations)) {
        if (variants.some(variant => name.includes(variant))) return key;
    }

    return 'default';
};

/**
 * Calculate nutrition facts for a list of ingredients
 * @param {Array} totalIngredients - Array of ingredients with weights
 * @param {number} servings - Number of servings
 * @returns {Object} - Nutrition facts per serving
 */
export const calculateNutritionFacts = (totalIngredients, servings = 1) => {
    let totalNutrition = {
        calories: 0,
        protein: 0,
        carbs: 0,
        fat: 0,
        fiber: 0
    };

    totalIngredients.forEach(([name, weightData]) => {
        const weightInGrams = weightData.number;
        const nutritionKey = findBestMatch(name);
        const nutrition = nutritionDatabase[nutritionKey];
        const ratio = weightInGrams / 100;

        totalNutrition.calories += nutrition.calories * ratio;
        totalNutrition.protein += nutrition.protein * ratio;
        totalNutrition.carbs += nutrition.carbs * ratio;
        totalNutrition.fat += nutrition.fat * ratio;
        totalNutrition.fiber += nutrition.fiber * ratio;
    });

    return {
        calories: Math.round(totalNutrition.calories / servings),
        protein: Math.round(totalNutrition.protein / servings * 10) / 10,
        carbs: Math.round(totalNutrition.carbs / servings * 10) / 10,
        fat: Math.round(totalNutrition.fat / servings * 10) / 10,
        fiber: Math.round(totalNutrition.fiber / servings * 10) / 10
    };
};

/**
 * Calculate daily value percentages
 * @param {Object} nutrition - Nutrition facts
 * @returns {Object} - Daily value percentages
 */
export const calculateDailyValues = (nutrition) => {
    const dailyValues = {
        calories: 2000,
        totalFat: 65,
        saturatedFat: 20,
        cholesterol: 300,
        sodium: 2300,
        totalCarbohydrates: 275,
        dietaryFiber: 28,
        protein: 50
    };

    return {
        calories: Math.round((nutrition.calories / dailyValues.calories) * 100),
        totalFat: Math.round((nutrition.totalFat / dailyValues.totalFat) * 100),
        saturatedFat: Math.round((nutrition.saturatedFat / dailyValues.saturatedFat) * 100),
        cholesterol: Math.round((nutrition.cholesterol / dailyValues.cholesterol) * 100),
        sodium: Math.round((nutrition.sodium / dailyValues.sodium) * 100),
        totalCarbohydrates: Math.round((nutrition.totalCarbohydrates / dailyValues.totalCarbohydrates) * 100),
        dietaryFiber: Math.round((nutrition.dietaryFiber / dailyValues.dietaryFiber) * 100),
        protein: Math.round((nutrition.protein / dailyValues.protein) * 100)
    };
};
